import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import BookNowLink from '../../components/booking/BookNowLink';
import { fetchDiscoverCampaigns, fetchMarketplaceServices } from '../../api/discover';
import { fetchMarketplace } from '../../api/products';
import MarketplaceLayout from '../../components/marketplace/MarketplaceLayout';
import MarketplaceOfferCard from '../../components/marketplace/MarketplaceOfferCard';
import MarketplaceOffersCarousel from '../../components/marketplace/MarketplaceOffersCarousel';
import { useCart } from '../../context/CartContext';
import PaginatedFooter from '../../components/ui/PaginatedFooter';
import { PRODUCT_CATEGORIES } from '../../constants/productCategories';
import { CATEGORIES as SERVICE_CATEGORIES } from '../../constants/serviceCategories';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import SearchAutocomplete from '../../components/ui/SearchAutocomplete';
import { buildLocalSuggestions } from '../../hooks/useListSearch';
import { resolveBookBranchId } from '../../utils/serviceBranches';

const stockClass = (badge) => {
  if (badge === 'In Stock') return 'text-emerald-400';
  if (badge === 'Low Stock') return 'text-amber-400';
  return 'text-red-400';
};

const SERVICE_CATALOG_LIMIT = 100;

const filterBySearch = (items, search, fields) => {
  const q = search.trim().toLowerCase();
  if (!q) return items;
  return items.filter((item) =>
    fields.some((field) => String(item[field] || '').toLowerCase().includes(q))
  );
};

const MarketplacePage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { addItem } = useCart();
  const [tab, setTab] = useState('products');
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [category, setCategory] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const isProducts = tab === 'products';
  const isServices = tab === 'services';
  const isOffers = tab === 'offers';
  const categories = isProducts ? PRODUCT_CATEGORIES : SERVICE_CATEGORIES;

  const { data: productsData, isLoading: productsLoading } = useQuery({
    queryKey: ['marketplace', appliedSearch, category, minPrice, maxPrice, tenantId, page, pageSize],
    queryFn: () =>
      fetchMarketplace({
        search: appliedSearch || undefined,
        category: category || undefined,
        minPrice: minPrice || undefined,
        maxPrice: maxPrice || undefined,
        tenantId: tenantId || undefined,
        isAvailable: 'true',
        page,
        limit: pageSize,
      }),
    enabled: isProducts,
  });

  const {
    data: servicesCatalog,
    isLoading: servicesCatalogLoading,
    isError: servicesError,
  } = useQuery({
    queryKey: ['marketplace-services-catalog', category, minPrice, maxPrice, tenantId],
    queryFn: () =>
      fetchMarketplaceServices({
        category: category || undefined,
        minPrice: minPrice || undefined,
        maxPrice: maxPrice || undefined,
        tenantId: tenantId || undefined,
        page: 1,
        limit: SERVICE_CATALOG_LIMIT,
      }),
    enabled: isServices,
    staleTime: 60_000,
  });

  const { data: campaignData, isLoading: campaignsLoading } = useQuery({
    queryKey: ['discover-campaigns', tenantId],
    queryFn: () => fetchDiscoverCampaigns({ tenantId: tenantId || undefined }),
    staleTime: 60_000,
  });

  const allCampaigns = useMemo(() => campaignData?.campaigns || [], [campaignData]);
  const carouselCampaigns = useMemo(
    () => allCampaigns.filter((c) => c.isHighlighted !== false).slice(0, 10),
    [allCampaigns]
  );

  const filteredOffers = useMemo(
    () => filterBySearch(allCampaigns, appliedSearch, ['name', 'bannerText', 'salonName']),
    [allCampaigns, appliedSearch]
  );

  const allServices = useMemo(() => servicesCatalog?.services || [], [servicesCatalog]);

  const filteredServices = useMemo(
    () => filterBySearch(allServices, appliedSearch, ['name', 'description', 'category', 'salonName']),
    [allServices, appliedSearch]
  );

  const servicesPagination = useMemo(
    () => ({
      total: filteredServices.length,
      page,
      limit: pageSize,
      pages: Math.max(1, Math.ceil(filteredServices.length / pageSize)),
    }),
    [filteredServices.length, page, pageSize]
  );

  const offersPagination = useMemo(
    () => ({
      total: filteredOffers.length,
      page,
      limit: pageSize,
      pages: Math.max(1, Math.ceil(filteredOffers.length / pageSize)),
    }),
    [filteredOffers.length, page, pageSize]
  );

  const services = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredServices.slice(start, start + pageSize);
  }, [filteredServices, page, pageSize]);

  const offers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredOffers.slice(start, start + pageSize);
  }, [filteredOffers, page, pageSize]);

  const products = productsData?.products || [];
  const pagination = isProducts
    ? productsData?.pagination
    : isServices
      ? servicesPagination
      : offersPagination;

  const productSalons = [...new Map(products.map((p) => [p.tenantId, p.salonName])).entries()];
  const serviceSalons = [...new Map(allServices.map((s) => [s.tenantId, s.salonName])).entries()];
  const offerSalons = [...new Map(allCampaigns.map((c) => [c.tenantId, c.salonName])).entries()];
  const salons = isProducts ? productSalons : isServices ? serviceSalons : offerSalons;

  const servicesInitialLoading = servicesCatalogLoading && !servicesCatalog;
  const showServicesGrid = isServices && !servicesInitialLoading && !servicesError;
  const showOffersGrid = isOffers && !campaignsLoading;

  useEffect(() => {
    const tid = searchParams.get('tenantId');
    if (tid) setTenantId(tid);
  }, [searchParams]);

  const switchTab = (next) => {
    setTab(next);
    setCategory('');
    setPage(1);
    setSearchInput('');
    setAppliedSearch('');
  };

  const autocompleteSuggestions = useMemo(() => {
    if (isProducts) {
      return buildLocalSuggestions(
        (productsData?.products || []).map((p) => ({
          label: p.name,
          value: p.name,
          hint: p.salonName,
        })),
        searchInput
      );
    }
    if (isServices) {
      return buildLocalSuggestions(
        allServices.map((s) => ({
          label: s.name,
          value: s.name,
          hint: s.salonName,
        })),
        searchInput
      );
    }
    return buildLocalSuggestions(
      allCampaigns.map((c) => ({
        label: c.bannerText || c.name,
        value: c.bannerText || c.name,
        hint: c.salonName,
      })),
      searchInput
    );
  }, [isProducts, isServices, productsData, allServices, allCampaigns, searchInput]);

  const searchPlaceholder = isProducts
    ? t('marketplace.searchProducts')
    : isServices
      ? t('marketplace.searchServices')
      : t('marketplace.searchOffers');

  return (
    <MarketplaceLayout>
      <div className="mx-auto flex max-w-7xl gap-8 px-6 py-8">
        <aside className={`w-64 shrink-0 space-y-4 ${filtersOpen ? '' : 'hidden lg:block'}`}>
          <button
            type="button"
            className="lg:hidden text-sm text-glow-400"
            onClick={() => setFiltersOpen(!filtersOpen)}
          >
            {filtersOpen ? t('common.hideFilters') : t('common.showFilters')}
          </button>
          <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4 space-y-4">
            <h3 className="font-medium">{t('common.filters')}</h3>
            {!isOffers && (
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => { setCategory(''); setPage(1); }}
                  className={`rounded-full px-2 py-1 text-xs ${!category ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800'}`}
                >
                  {t('common.all')}
                </button>
                {categories.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => { setCategory(c); setPage(1); }}
                    className={`rounded-full px-2 py-1 text-xs ${category === c ? 'bg-glow-600/30 text-glow-300' : 'bg-zinc-100 dark:bg-zinc-800'}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
            {!isOffers && (
              <>
                <div>
                  <label className="text-xs text-zinc-600 dark:text-zinc-500">{t('common.minPrice')}</label>
                  <input type="number" value={minPrice} onChange={(e) => { setMinPrice(e.target.value); setPage(1); }} className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-zinc-600 dark:text-zinc-500">{t('common.maxPrice')}</label>
                  <input type="number" value={maxPrice} onChange={(e) => { setMaxPrice(e.target.value); setPage(1); }} className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1 text-sm" />
                </div>
              </>
            )}
            <div>
              <label className="text-xs text-zinc-600 dark:text-zinc-500">{t('common.salon')}</label>
              <select value={tenantId} onChange={(e) => { setTenantId(e.target.value); setPage(1); }} className="mt-1 w-full rounded border border-zinc-300 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-950 px-2 py-1 text-sm">
                <option value="">{t('common.allSalons')}</option>
                {salons.map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </select>
            </div>
          </div>
        </aside>

        <main className="flex-1">
          <MarketplaceOffersCarousel campaigns={carouselCampaigns} />

          <div className="mb-6 flex gap-2 border-b border-zinc-200 dark:border-zinc-800">
            {[
              { id: 'products', labelKey: 'marketplace.products' },
              { id: 'services', labelKey: 'marketplace.services' },
              { id: 'offers', labelKey: 'marketplace.offers' },
            ].map(({ id, labelKey }) => (
              <button
                key={id}
                type="button"
                onClick={() => switchTab(id)}
                className={`border-b-2 px-4 py-2 text-sm ${
                  tab === id ? 'border-glow-500 text-glow-300' : 'border-transparent text-zinc-600 dark:text-zinc-500'
                }`}
              >
                {t(labelKey)}
              </button>
            ))}
          </div>

          <SearchAutocomplete
            className="mb-6"
            inputClassName="rounded-xl px-4 py-3"
            value={searchInput}
            onChange={setSearchInput}
            onSearch={(v) => {
              const next =
                v && typeof v === 'object'
                  ? String(v.value ?? v.label ?? '').trim()
                  : String(v ?? '').trim();
              setAppliedSearch(next);
              setSearchInput(next);
              setPage(1);
            }}
            suggestions={autocompleteSuggestions}
            placeholder={searchPlaceholder}
          />

          {isProducts && productsLoading && <p className="text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}
          {isServices && servicesInitialLoading && <p className="text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}
          {isOffers && campaignsLoading && <p className="text-zinc-600 dark:text-zinc-500">{t('common.loading')}</p>}
          {isServices && servicesError && (
            <p className="text-red-400">{t('marketplace.servicesLoadError')}</p>
          )}

          {isProducts && (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {products.map((product) => (
                <div key={product._id} className="overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                  <Link to={`/marketplace/products/${product._id}`}>
                    {product.images?.[0]?.url ? (
                      <img src={product.images[0].url} alt="" className="h-40 w-full object-cover" />
                    ) : (
                      <div className="flex h-40 items-center justify-center bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-500">{t('common.noImage')}</div>
                    )}
                  </Link>
                  <div className="p-4">
                    <Link to={`/marketplace/products/${product._id}`} className="font-medium hover:text-glow-400">
                      {product.name}
                    </Link>
                    {product.campaignPricing ? (
                      <div className="mt-1">
                        <p className="text-xs text-emerald-600 dark:text-emerald-400">
                          {product.campaignPricing.discountMode === 'PERCENTAGE'
                            ? `${product.campaignPricing.discountValue}% OFF`
                            : `LKR ${Number(product.campaignPricing.discountValue || 0).toLocaleString()} OFF`}
                        </p>
                        <p className="text-lg font-semibold text-glow-300">
                          LKR {Number(product.campaignPricing.finalPrice || 0).toLocaleString()}
                          <span className="ml-2 text-sm font-normal text-zinc-500 line-through">
                            LKR {Number(product.campaignPricing.basePrice || 0).toLocaleString()}
                          </span>
                        </p>
                      </div>
                    ) : (
                      <p className="mt-1 text-lg font-semibold text-glow-300">
                        LKR {product.price?.toLocaleString()}
                      </p>
                    )}
                    <p className="text-xs text-zinc-600 dark:text-zinc-500">
                      {product.salonName} · ★ {product.salonRating}
                    </p>
                    <p className={`mt-1 text-xs ${stockClass(product.stockBadge)}`}>{product.stockBadge}</p>
                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        disabled={product.stock <= 0}
                        onClick={() => addItem(product, 1)}
                        className="flex-1 rounded-lg border border-zinc-300 dark:border-zinc-700 py-1.5 text-xs hover:bg-zinc-100 dark:hover:bg-zinc-800 disabled:opacity-40"
                      >
                        {t('common.addToCart')}
                      </button>
                      <Link
                        to={`/marketplace/products/${product._id}`}
                        className="flex-1 rounded-lg bg-glow-600 py-1.5 text-center text-xs text-white"
                      >
                        {t('common.orderNow')}
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {showServicesGrid && (
            <>
              {filteredServices.length === 0 && (
                <p className="mb-4 text-zinc-600 dark:text-zinc-500">
                  {appliedSearch.trim() ? t('marketplace.noServicesSearch') : t('marketplace.noServices')}
                </p>
              )}
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {services.map((service) => (
                  <div key={service._id} className="overflow-hidden rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
                    {service.imageUrl ? (
                      <img src={service.imageUrl} alt="" className="h-40 w-full object-cover" />
                    ) : (
                      <div className="flex h-40 items-center justify-center bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-500">{t('common.noImage')}</div>
                    )}
                    <div className="p-4">
                      <p className="font-medium">{service.name}</p>
                      {service.campaignPricing ? (
                        <div className="mt-1">
                          <p className="text-xs text-emerald-600 dark:text-emerald-400">
                            {service.campaignPricing.discountMode === 'PERCENTAGE'
                              ? `${service.campaignPricing.discountValue}% OFF`
                              : `LKR ${Number(service.campaignPricing.discountValue || 0).toLocaleString()} OFF`}
                          </p>
                          <p className="text-lg font-semibold text-glow-300">
                            LKR {Number(service.campaignPricing.finalPrice || 0).toLocaleString()}
                            <span className="ml-2 text-sm font-normal text-zinc-500 line-through">
                              LKR {Number(service.campaignPricing.basePrice || 0).toLocaleString()}
                            </span>
                          </p>
                        </div>
                      ) : (
                        <p className="mt-1 text-lg font-semibold text-glow-300">
                          LKR {service.price?.toLocaleString()}
                        </p>
                      )}
                      <p className="text-xs text-zinc-600 dark:text-zinc-500">
                        {service.duration} min · {service.category}
                      </p>
                      <p className="text-xs text-zinc-600 dark:text-zinc-500">
                        {service.salonName} · ★ {service.salonRating}
                      </p>
                      <BookNowLink
                        tenantId={service.tenantId}
                        branchId={resolveBookBranchId(service)}
                        serviceId={service._id}
                        className="mt-3 block w-full rounded-lg bg-glow-600 py-1.5 text-center text-xs text-white"
                      >
                        {t('common.bookNow')}
                      </BookNowLink>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {showOffersGrid && (
            <>
              {filteredOffers.length === 0 && (
                <p className="mb-4 text-zinc-600 dark:text-zinc-500">
                  {appliedSearch.trim() ? t('marketplace.noOffersSearch') : t('marketplace.noOffers')}
                </p>
              )}
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {offers.map((offer) => (
                  <MarketplaceOfferCard key={offer._id} offer={offer} />
                ))}
              </div>
            </>
          )}

          {(isProducts || showServicesGrid || showOffersGrid) && (
            <PaginatedFooter
              pagination={pagination}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
            />
          )}
        </main>
      </div>
    </MarketplaceLayout>
  );
};

export default MarketplacePage;
